import cx from "classnames";
import { Fragment } from "react";
import PropTypes from "prop-types";
import "lazysizes";
import { objToPairStr } from "utils";
import styled from "styled-components";
import { cleanImageURL } from "@literati/public-ui-shared-react";

const StyledImg = styled.img`
  &.lazyload:not([src]) {
    visibility: hidden;
    overflow: hidden;
  }
`;

const ResponsiveImage = (props) => {
  const {
    source,
    additionalSources = [],
    sizes,
    alt,
    className,
    lazy,
    quality,
  } = props;

  const makeSrcSet = ({
    url,
    thumbnailSizes,
    format,
    prependQueries = {},
    appendQueries = {},
  }) => {
    const makeQueries = (width) => {
      const queryObj = {
        ...prependQueries,
        fm: format,
        q: quality,
        w: width,
        ...appendQueries,
      };
      return objToPairStr(queryObj, "=", "&");
    };
    const cleanedUrl = cleanImageURL(url);
    const srcSet = thumbnailSizes.map((width) =>
      [
        `${cleanedUrl}?${makeQueries(width)} ${width}w`,
        `${cleanedUrl}?${makeQueries(width * 2)} ${width * 2}w`,
      ].join(", ")
    );
    return {
      [lazy ? "data-srcset" : "srcSet"]: srcSet.join(", "),
    };
  };

  const getSizes = (screenSizes) => ({
    [lazy ? "data-sizes" : "sizes"]: screenSizes || sizes,
  });

  const getOriginalFm = (url) => (url?.indexOf(".png") > -1 ? "png" : "jpg");

  const getOriginalProperties = ({ url }) => {
    const originalFormat = getOriginalFm(url);
    return {
      origFormat: originalFormat,
      origContentType: `image/${originalFormat}`,
    };
  };

  const defaultOrigFm = getOriginalFm(source?.url);

  return (
    <picture>
      {additionalSources.map((addSource) => {
        const webpSrcSet = makeSrcSet({ ...addSource, format: "webp" });
        const { origFormat, origContentType } =
          getOriginalProperties(addSource);
        const secondarySrcSet = makeSrcSet({
          ...addSource,
          format: origFormat,
        });

        return (
          <Fragment key={addSource.url}>
            <source
              media={addSource.media}
              type="image/webp"
              {...webpSrcSet}
              {...getSizes(addSource.thumbnailSizes)}
            />
            <source
              type={origContentType}
              media={addSource.media}
              {...secondarySrcSet}
              {...getSizes(addSource.thumbnailSizes)}
            />
          </Fragment>
        );
      })}
      <source
        type="image/webp"
        media={source.media}
        {...makeSrcSet({ ...source, format: "webp" })}
        {...getSizes()}
      />
      <StyledImg
        className={cx(className, { lazyload: lazy })}
        alt={alt}
        {...makeSrcSet({ ...source, format: defaultOrigFm })}
        {...getSizes()}
      />
    </picture>
  );
};

const sourceType = (isAdditional) =>
  PropTypes.shape({
    media: isAdditional ? PropTypes.string.isRequired : PropTypes.string,
    url: PropTypes.string.isRequired,
    thumbnailSizes: PropTypes.arrayOf(PropTypes.number).isRequired,
    prependQueries: PropTypes.object,
    appendQueries: PropTypes.object,
  });

ResponsiveImage.propTypes = {
  lazy: PropTypes.bool,
  className: PropTypes.string,
  source: sourceType().isRequired,
  additionalSources: PropTypes.arrayOf(sourceType(true)),
  sizes: PropTypes.string,
  alt: PropTypes.string,
  style: PropTypes.object,
  quality: PropTypes.number,
};

ResponsiveImage.defaultProps = {
  quality: 100,
};

export default ResponsiveImage;
